import * as d3 from 'd3';
import type React from 'react';

export interface BrushConfig {
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  plotWidth: number;
  plotHeight: number;
  sessionId: string;
  xScale: d3.ScaleLinear<number, number>;
  zoomBehavior: d3.ZoomBehavior<SVGSVGElement, unknown>;
  zoomTransformRef: React.MutableRefObject<d3.ZoomTransform>;
  /** Called after brush selection zoom to lock the visible window. */
  onZoomToWindow?: (windowStart: number, windowEnd: number) => void;
  /** Called to force React to re-render the chart after brush zoom. */
  onForceReRender?: () => void;
}

export function setupBrush(config: BrushConfig): {
  cleanup: () => void;
} {
  const {
    svg,
    g,
    plotWidth,
    plotHeight,
    sessionId,
    xScale,
    zoomBehavior,
    zoomTransformRef,
    onZoomToWindow,
    onForceReRender,
  } = config;

  const brush = d3
    .brushX()
    .extent([
      [0, 0],
      [plotWidth, plotHeight],
    ])
    .filter((event) => {
      return event.shiftKey && event.type !== 'wheel';
    })
    .on('start', function (event) {
      if (!event?.sourceEvent?.shiftKey) {
        return;
      }
      svg.classed('brushing-active', true);
    })
    .on('brush', function (event) {
      if (!event?.sourceEvent?.shiftKey) {
        return;
      }
    })
    .on('end', function (event) {
      svg.classed('brushing-active', false);

      if (!event.selection || !event?.sourceEvent?.shiftKey) {
        return;
      }

      const [start, end] = event.selection as [number, number];

      if (Math.abs(end - start) < 10) {
        return;
      }

      const currentTransform = d3.zoomTransform(svg.node() as Element);
      const currentXScale = currentTransform.rescaleX(xScale);

      const x0 = currentXScale.invert(start);
      const _x1 = currentXScale.invert(end);

      const k = (currentTransform.k * plotWidth) / (end - start);
      const x0PixelInOriginalSpace = xScale(x0);
      const tx = -x0PixelInOriginalSpace * k;

      const newTransform = d3.zoomIdentity.translate(tx, 0).scale(k);

      zoomTransformRef.current = newTransform;

      brushLayer.call(brush.move as any, null);

      // Apply transform immediately to avoid stale __zoom if user pans before transition completes
      svg.call(zoomBehavior.transform, newTransform);

      const newXScale = newTransform.rescaleX(xScale);
      const [visibleStart, visibleEnd] = newXScale.domain();

      if (onZoomToWindow) {
        onZoomToWindow(visibleStart, visibleEnd);
      }

      // Force React to re-render so the new zoom state is absorbed into
      // React's state management, creating a clean base for subsequent pans.
      if (onForceReRender) {
        onForceReRender();
      }
    });

  const brushLayer = g
    .append('g')
    .attr('class', 'brush-layer')
    .attr('clip-path', `url(#plot-clip-${sessionId})`)
    .call(brush as any);

  brushLayer
    .selectAll('.selection')
    .attr('fill', 'rgba(59, 130, 246, 0.2)')
    .attr('stroke', '#3b82f6')
    .attr('stroke-width', 2)
    .attr('rx', 2)
    .attr('ry', 2);

  brushLayer.selectAll('.handle').attr('fill', '#3b82f6').attr('width', 6).attr('rx', 3);

  const brushOverlay = brushLayer.selectAll('.overlay');

  brushOverlay.style('pointer-events', 'none').style('cursor', 'default');

  let isShiftPressed = false;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Shift' && !isShiftPressed) {
      isShiftPressed = true;
      brushOverlay.style('pointer-events', 'all');
      brushOverlay.style('cursor', 'crosshair');
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      isShiftPressed = false;
      brushOverlay.style('pointer-events', 'none');
      brushOverlay.style('cursor', 'default');
    }
  };

  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('keyup', handleKeyUp);

  svg.on('pointerenter.brush', (event) => {
    if (event.shiftKey) {
      isShiftPressed = true;
      brushOverlay.style('pointer-events', 'all');
      brushOverlay.style('cursor', 'crosshair');
    } else {
      isShiftPressed = false;
      brushOverlay.style('pointer-events', 'none');
      brushOverlay.style('cursor', 'default');
    }
  });

  return {
    cleanup: () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    },
  };
}
